import { useEffect, useState } from 'react';
import { LayoutHeader as Header } from '@/layouts';
import { Footer } from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  MapPin,
  Phone,
  MessageCircle,
  Clock,
  Instagram,
  Facebook,
  Youtube,
  Building2,
  Navigation,
  Linkedin,
  Twitter,
  ExternalLink,
  Heart,
  Bell
} from 'lucide-react';
import { useAppData } from '@/contexts/AppDataContext';
import { useTheme } from '@/contexts/ThemeContext';
import { openWhatsApp } from '@/lib/whatsapp';
import { requestUserLocation, getDistanceToStore, formatDistance } from '@/lib/geolocation';
import { formatOpeningHoursDayRange, hasOpeningHoursData } from '@/lib/openingHours';
import googlePlay from '@/assets/google-play.png';
import appStore from '@/assets/app-store.png';
import { openStoreDirections } from '@/lib/storeMapUrls';
import { hasAppStoreLink as hasResolvedAppStoreLink, resolveAppStoreUrl } from '@/lib/appStoreLinks';

const Contact = () => {
  const { config } = useAppData();
  const allStores = config.stores || [];
  const { colors } = useTheme();
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);

  const buttonColor = colors.button;
  const primaryColor = colors.primary;
  const secondaryColor = colors.secondary;
  const titleColor = colors.title;
  const hasGooglePlayLink = !!config.googlePlay?.url?.trim();
  const appStoreUrl = resolveAppStoreUrl(config.appStore);
  const hasAppStoreLink = hasResolvedAppStoreLink(config.appStore);

  // Request user location only when needed (user interaction)
  // Don't request automatically on page load to avoid trust issues

  useEffect(() => {
    document.title = `Contato - ${config.name}`;
  }, [config.name]);

  const formatOpeningHours = (openingHours: any) => {
    const days = [
      { key: 'monday', label: 'Seg' },
      { key: 'tuesday', label: 'Ter' },
      { key: 'wednesday', label: 'Qua' },
      { key: 'thursday', label: 'Qui' },
      { key: 'friday', label: 'Sex' },
      { key: 'saturday', label: 'Sáb' },
      { key: 'sunday', label: 'Dom' },
    ];

    return days.map((day) => ({
      day: day.label,
      hours: formatOpeningHoursDayRange(openingHours, day.key),
    }));
  };

  return (
    <div className="min-h-screen bg-muted/30">
      <Header config={config} />

      {/* About Section */}
      {config.about && (
        <section className="pt-36 pb-12">
          <div className="container mx-auto px-4">
            <Card className="border border-border/50">
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-6">
                  <div className="flex-shrink-0">
                    <img
                      src={config.icon?.light}
                      alt={config.name}
                      className="h-16 w-auto object-contain"
                    />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold mb-2" style={{ color: titleColor }}>
                      Sobre a {config.name}
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      Nossa história, valores e compromisso com você
                    </p>
                  </div>
                </div>
                <Separator className="mb-6" />
                <div className="prose max-w-none">
                  <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                    {config.about}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* Stores Section */}
      <section className="pb-12">
        <div className="container mx-auto px-4">
          <div className="space-y-6">
            {allStores.map((store: any, index: number) => {
              const distance = getDistanceToStore(userLocation, store);

              return (
                <Card key={store._id || index} className="border border-border/50 overflow-hidden">
                  <CardContent className="p-0">
                    <div className="grid grid-cols-1 md:grid-cols-[620px_1fr] gap-0">
                      {/* Store Image - Fixed width 400px on desktop */}
                      <div className="relative aspect-[16/9] md:aspect-auto md:h-full overflow-hidden bg-muted/30">
                        {store.frontage && (
                          <>
                            <img
                              src={store.frontage}
                              alt={store.company}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

                            {/* Store Name - Mobile Only */}
                            <div className="absolute bottom-20 left-4 right-4 md:hidden">
                              <h3 className="text-xl font-bold text-white mb-1">{store.company}</h3>
                              {distance !== null && (
                                <Badge variant="secondary" className="bg-white/20 text-white border-white/30">
                                  <Navigation className="h-3 w-3 mr-1" />
                                  {formatDistance(distance)}
                                </Badge>
                              )}
                            </div>

                            {/* Contact Buttons - Footer on Image */}
                            <div className="absolute bottom-4 left-4 right-4 flex gap-3">
                              {/* WhatsApp */}
                              {store.whatsapp?.ios && (
                                <Button
                                  onClick={() => openWhatsApp(store.whatsapp?.ios)}
                                  className="flex-1 bg-green-600/95 hover:bg-green-700 text-white font-semibold backdrop-blur-sm shadow-lg"
                                  size="lg"
                                >
                                  <MessageCircle className="h-4 w-4 mr-2" />
                                  WhatsApp
                                </Button>
                              )}

                              {/* Phone */}
                              {store.phone?.ios && (
                                <Button
                                  onClick={() => window.open(store.phone?.ios, '_blank')}
                                  className="flex-1 bg-white/95 hover:bg-white text-foreground font-semibold backdrop-blur-sm shadow-lg"
                                  size="lg">
                                  <Phone className="h-4 w-4 mr-2" />
                                  Ligar
                                </Button>
                              )}
                            </div>
                          </>
                        )}
                      </div>

                      {/* Store Info - Full width on mobile, remaining space on desktop */}
                      <div className="p-6 md:p-8 flex flex-col justify-between">
                        {/* Store Name - Only visible on desktop */}
                        <div className="hidden md:block mb-6">
                          <div className="flex items-center gap-3">
                            <Building2 className="h-6 w-6 text-muted-foreground" />
                            <h3 className="text-2xl font-bold text-foreground">{store.company}</h3>
                            {distance !== null && (
                              <Badge variant="secondary" style={{ background: `${primaryColor}15`, color: primaryColor }}>
                                <Navigation className="h-3 w-3 mr-1" />
                                {formatDistance(distance)}
                              </Badge>
                            )}
                          </div>
                        </div>

                        <div className="space-y-6">
                          {/* Address Card */}
                          {store.place?.address && (
                            <button
                              onClick={() => {
                                const place = store.place;
                                openStoreDirections(place);
                              }}
                              className="w-full group"
                            >
                              <div className="flex items-start gap-4 p-4 rounded-xl border border-border/50 hover:border-primary/50 bg-muted/20 hover:bg-muted/40 transition-all">
                                <div className="flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center" style={{ background: `${primaryColor}15` }}>
                                  <MapPin className="h-5 w-5" style={{ color: primaryColor }} />
                                </div>
                                <div className="flex-1 text-left">
                                  <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Endereço</p>
                                  <p className="text-sm font-medium text-foreground leading-relaxed">
                                    {store.place.address}
                                  </p>
                                </div>
                                <ExternalLink className="h-5 w-5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                              </div>
                            </button>
                          )}

                          {/* Opening Hours */}
                          {hasOpeningHoursData(store.openingHours) && (
                            <div>
                              <div className="flex items-center gap-2 mb-3">
                                <Clock className="h-5 w-5 text-muted-foreground" />
                                <h4 className="text-sm font-semibold">Horário de Funcionamento</h4>
                              </div>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                                {formatOpeningHours(store.openingHours).map((schedule, idx) => (
                                  <div key={idx} className="flex justify-between p-2 rounded-lg bg-muted/30">
                                    <span className="text-muted-foreground font-medium">{schedule.day}</span>
                                    <span className={`font-semibold ${schedule.hours === 'Fechado' ? 'text-red-500' : 'text-foreground'}`}>
                                      {schedule.hours}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Social Media Section */}
      {config.socialNetworks && (
        <section className="pb-12">
          <div className="container mx-auto px-4">
            <Card className="border border-border/50">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-foreground mb-2">
                    Redes Sociais
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Siga-nos e fique por dentro das novidades
                  </p>
                </div>

                <div className="flex flex-wrap items-center justify-center gap-3">
                  {config.socialNetworks.instagram && (
                    <a
                      href={config.socialNetworks.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group"
                    >
                      <Button
                        variant="outline"
                        size="lg"
                        className="hover:bg-pink-500 hover:text-white hover:border-pink-500 transition-all"
                      >
                        <Instagram className="h-5 w-5 mr-2" />
                        Instagram
                      </Button>
                    </a>
                  )}
                  {config.socialNetworks.facebook && (
                    <a
                      href={config.socialNetworks.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group"
                    >
                      <Button
                        variant="outline"
                        size="lg"
                        className="hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all"
                      >
                        <Facebook className="h-5 w-5 mr-2" />
                        Facebook
                      </Button>
                    </a>
                  )}
                  {config.socialNetworks.youtube && (
                    <a
                      href={config.socialNetworks.youtube}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group"
                    >
                      <Button
                        variant="outline"
                        size="lg"
                        className="hover:bg-red-600 hover:text-white hover:border-red-600 transition-all"
                      >
                        <Youtube className="h-5 w-5 mr-2" />
                        YouTube
                      </Button>
                    </a>
                  )}
                  {config.socialNetworks.linkedin && (
                    <a
                      href={config.socialNetworks.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group"
                    >
                      <Button
                        variant="outline"
                        size="lg"
                        className="hover:bg-blue-700 hover:text-white hover:border-blue-700 transition-all"
                      >
                        <Linkedin className="h-5 w-5 mr-2" />
                        LinkedIn
                      </Button>
                    </a>
                  )}
                  {config.socialNetworks.twitter && (
                    <a
                      href={config.socialNetworks.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group"
                    >
                      <Button
                        variant="outline"
                        size="lg"
                        className="hover:bg-black hover:text-white hover:border-black transition-all"
                      >
                        <Twitter className="h-5 w-5 mr-2" />
                        Twitter
                      </Button>
                    </a>
                  )}
                  {config.socialNetworks.tiktok && (
                    <a
                      href={config.socialNetworks.tiktok}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group"
                    >
                      <Button
                        variant="outline"
                        size="lg"
                        className="hover:bg-black hover:text-white hover:border-black transition-all"
                      >
                        <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                        </svg>
                        TikTok
                      </Button>
                    </a>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* Download App Section */}
      {(hasGooglePlayLink || hasAppStoreLink) && (
        <section className="pb-12">
          <div className="container mx-auto px-4">
            <Card className="border border-border/50 overflow-hidden" style={{ background: `linear-gradient(135deg, ${primaryColor}08, ${secondaryColor}08)` }}>
              <CardContent className="p-8 md:p-12">
                <div className="flex flex-col md:flex-row items-center justify-between gap-8">
                  {/* Left: Text Content */}
                  <div className="flex-1 text-center md:text-left">
                    <h2 className="text-3xl font-bold text-foreground mb-3">
                      Baixe Nosso App
                    </h2>
                    <p className="text-muted-foreground text-lg mb-6">
                      Tenha acesso ao nosso estoque completo na palma da sua mão. Navegue, favorite e entre em contato diretamente pelo app.
                    </p>

                    {/* App Badges */}
                    <div className="flex flex-wrap items-center justify-center md:justify-start gap-4">
                      {hasGooglePlayLink && (
                        <a
                          href={config.googlePlay.url}
                          target="_blank"
                          rel="noopener noreferrer">
                          <img
                            src={googlePlay}
                            alt="Baixar na Google Play"
                            className="h-14 w-auto"
                          />
                        </a>
                      )}
                      {hasAppStoreLink && (
                        <a
                          href={appStoreUrl}
                          target="_blank"
                          rel="noopener noreferrer">
                          <img
                            src={appStore}
                            alt="Baixar na App Store"
                            className="h-14 w-auto"
                          />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      <Footer config={config} />
    </div>
  );
};

export default Contact;
